import { Controller } from "@hotwired/stimulus";

// Handles file picking for the import upload form:
//   1. Reveals a confirmation card with file name and size on `change`.
//   2. Accepts files dropped on the drop-zone, assigns them to the hidden
//      <input type="file">, and fires `change` to drive the same UI update.
//   3. Adds a highlighted style to the drop-zone during a drag.
//
// Targets: input (the file input), card, name, meta, dropzone (visual area).
export default class extends Controller {
  static targets = ["input", "card", "name", "meta", "dropzone"];

  connect() {
    if (this.hasDropzoneTarget) {
      ["dragenter", "dragover"].forEach((evt) => {
        this.dropzoneTarget.addEventListener(evt, this.onDragOver);
      });
      ["dragleave", "dragend", "drop"].forEach((evt) => {
        this.dropzoneTarget.addEventListener(evt, this.onDragLeave);
      });
      this.dropzoneTarget.addEventListener("drop", this.onDrop);
    }
  }

  disconnect() {
    if (this.hasDropzoneTarget) {
      ["dragenter", "dragover"].forEach((evt) => {
        this.dropzoneTarget.removeEventListener(evt, this.onDragOver);
      });
      ["dragleave", "dragend", "drop"].forEach((evt) => {
        this.dropzoneTarget.removeEventListener(evt, this.onDragLeave);
      });
      this.dropzoneTarget.removeEventListener("drop", this.onDrop);
    }
  }

  show(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      this.cardTarget.classList.add("d-none");
      return;
    }
    this.nameTarget.textContent = file.name;
    this.metaTarget.textContent = `${this.formatSize(file.size)} · Ready for validation`;
    this.cardTarget.classList.remove("d-none");
  }

  onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.dropzoneTarget.classList.add("border-primary", "bg-white");
  };

  onDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.dropzoneTarget.classList.remove("border-primary", "bg-white");
  };

  onDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    this.inputTarget.files = dataTransfer.files;
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  };

  formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
